import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Watch, RefreshCw, Moon, Activity, HeartPulse, BatteryCharging } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Slider } from "./ui/slider";

const FATIGUE = ["Fresh", "Normal", "Tired", "Wrecked"];

function sleepLabel(v) {
  if (v <= 3) return "Poor";
  if (v <= 6) return "Ok";
  if (v <= 8) return "Good";
  return "Excellent";
}

function GarminStat({ icon: Icon, label, value, unit }) {
  if (value == null) return null;
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2 flex items-center gap-2">
      <Icon size={15} className="text-brand-orange shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground leading-none">{label}</p>
        <p className="font-heading font-bold text-foreground text-sm mt-0.5">{value}{unit}</p>
      </div>
    </div>
  );
}

export default function CheckinDialog({ open, onOpenChange, onDone }) {
  const [sleepQ, setSleepQ] = useState(6);
  const [fatigue, setFatigue] = useState("Normal");
  const [pain, setPain] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [garmin, setGarmin] = useState(null); // {connected, readiness}
  const [syncing, setSyncing] = useState(false);

  const pullReadiness = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/garmin/today-readiness");
      setGarmin(data);
      const sc = data.readiness?.sleep_score;
      if (sc != null) setSleepQ(Math.max(1, Math.min(10, Math.round(sc / 10))));
    } catch { setGarmin({ connected: false, readiness: {} }); }
    finally { setSyncing(false); }
  };

  useEffect(() => { if (open) pullReadiness(); /* eslint-disable-next-line */ }, [open]);

  const r = garmin?.readiness || {};

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkin", {
        fatigue,
        sleep_quality: sleepQ,
        pain: pain || "none",
        note: note || null,
        sleep_score: r.sleep_score ?? null,
        sleep_hours: r.sleep_hours ?? null,
        hrv: r.hrv ?? null,
        resting_hr: r.resting_hr ?? null,
        body_battery: r.body_battery ?? null,
        stress: r.stress ?? null,
      });
      onDone(data);
      onOpenChange(false);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight">How are you today?</DialogTitle>
          <DialogDescription>Sleep, energy and any niggles — I'll adapt today's session to fit.</DialogDescription>
        </DialogHeader>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5 pt-1">
          {/* Garmin readiness */}
          {garmin?.connected && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="checkin-garmin">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  <Watch size={13} className="text-brand-orange" /> From your Garmin
                </div>
                <button data-testid="checkin-garmin-sync" onClick={pullReadiness} disabled={syncing} className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline">
                  <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> Sync
                </button>
              </div>
              {Object.keys(r).length ? (
                <div className="grid grid-cols-2 gap-2">
                  <GarminStat icon={Moon} label="Sleep score" value={r.sleep_score} />
                  <GarminStat icon={Moon} label="Slept" value={r.sleep_hours} unit="h" />
                  <GarminStat icon={HeartPulse} label="HRV" value={r.hrv} />
                  <GarminStat icon={Activity} label="Resting HR" value={r.resting_hr} />
                  <GarminStat icon={BatteryCharging} label="Body battery" value={r.body_battery} />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{syncing ? "Pulling today's stats…" : "No stats yet today — tap Sync, or fill in below."}</p>
              )}
            </div>
          )}

          {/* Sleep quality slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-foreground">Sleep last night</p>
              <span className="text-sm font-bold text-brand-orange" data-testid="checkin-sleep-value">{sleepQ}/10 · {sleepLabel(sleepQ)}</span>
            </div>
            <Slider data-testid="checkin-sleep-slider" value={[sleepQ]} onValueChange={(v) => setSleepQ(v[0])} min={1} max={10} step={1} />
            <p className="text-xs text-muted-foreground mt-1.5">1–3 poor · 4–6 ok · 7–8 good · 9–10 excellent{r.sleep_score != null ? ` — Garmin scored ${r.sleep_score}/100` : ""}</p>
          </div>

          {/* Fatigue */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">How do your legs / body feel?</p>
            <div className="flex flex-wrap gap-2" data-testid="checkin-fatigue">
              {FATIGUE.map((o) => (
                <button key={o} data-testid={`checkin-fatigue-${o}`} onClick={() => setFatigue(o)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${fatigue === o ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground hover:border-primary/50"}`}>{o}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-foreground mb-2">Any pain, illness or worry? <span className="text-muted-foreground font-normal">(optional)</span></p>
            <Textarea data-testid="checkin-pain" value={pain} onChange={(e) => setPain(e.target.value)} placeholder="e.g. slight knee niggle" rows={2} />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">Anything else for your coach? <span className="text-muted-foreground font-normal">(optional)</span></p>
            <Textarea data-testid="checkin-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. stressful day at work" rows={2} />
          </div>
          <Button data-testid="checkin-submit" onClick={submit} disabled={loading} className="w-full h-12 rounded-xl text-base font-semibold active:scale-95 transition-transform">
            {loading ? "Caden is thinking…" : "Ask Caden"}
          </Button>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
